import { css, jsx, keyframes } from '@emotion/core';
import PropTypes from 'prop-types';
import { UNMOUNTED, EXITED, ENTERING, ENTERED, EXITING } from 'react-transition-group/Transition';
import React, { Fragment, useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { TransitionGroup, Transition } from 'react-transition-group';
import hoistNonReactStatics from 'hoist-non-react-statics';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __spreadArrays() {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
}
function __makeTemplateObject(cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
}

var ToastType;
(function (ToastType) {
    ToastType["ERROR"] = "error";
    ToastType["INFO"] = "info";
    ToastType["SUCCESS"] = "success";
    ToastType["WARNING"] = "warning";
})(ToastType || (ToastType = {}));
var Placement;
(function (Placement) {
    Placement["BOTTOM_LEFT"] = "bottom-left";
    Placement["BOTTOM_CENTER"] = "bottom-center";
    Placement["BOTTOM_RIGHT"] = "bottom-right";
    Placement["TOP_LEFT"] = "top-left";
    Placement["TOP_CENTER"] = "top-center";
    Placement["TOP_RIGHT"] = "top-right";
})(Placement || (Placement = {}));
var childrenProps = PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
]);
var placements = [
    Placement.BOTTOM_LEFT,
    Placement.BOTTOM_CENTER,
    Placement.BOTTOM_RIGHT,
    Placement.TOP_LEFT,
    Placement.TOP_CENTER,
    Placement.TOP_RIGHT,
];
var placementsProps = PropTypes.oneOf(placements);
var toastTypes = ['error', 'info', 'success', 'warning'];
var toastTypesProps = PropTypes.oneOf(toastTypes);
var transitionStatuses = [UNMOUNTED, EXITED, ENTERING, ENTERED, EXITING];
var transitionStatusProps = PropTypes.oneOf(transitionStatuses);
var stylerProps = PropTypes.oneOfType([PropTypes.object, PropTypes.func]);

var isDev = process.env.NODE_ENV !== 'production';
var noop = function () { return undefined; };
var getId = function () { return Math.random().toString(36).substr(2, 6); };
var getStylesMapCSS = function (styler, props) { return (typeof styler === 'function' ? styler(props) : styler) || {}; };
var getStylesCSS = function (styler, props) {
    return (typeof styler === 'function' ? styler(props) : styler) || {};
};
var isBrowser = typeof window !== 'undefined' && typeof window.document !== 'undefined';
var TimerState;
(function (TimerState) {
    TimerState[TimerState["READY"] = 0] = "READY";
    TimerState[TimerState["RUNNING"] = 1] = "RUNNING";
    TimerState[TimerState["PAUSED"] = 2] = "PAUSED";
    TimerState[TimerState["COMPLETED"] = 3] = "COMPLETED";
})(TimerState || (TimerState = {}));
var Timer =  (function () {
    function Timer(callback, timeout) {
        var _this = this;
        this.callback = callback;
        this.state = TimerState.READY;
        this.start = function () {
            _this.clear();
            if (_this.timeLeft > 0) {
                _this.startDate = Date.now();
                _this.timerId = window.setTimeout(function () {
                    _this.callback();
                    _this.timeLeft = 0;
                    _this.state = TimerState.COMPLETED;
                }, _this.timeLeft);
                _this.state = TimerState.RUNNING;
            }
        };
        this.pause = function () {
            if (_this.startDate) {
                _this.clear();
                _this.state = TimerState.PAUSED;
                _this.timeLeft -= Date.now() - _this.startDate;
            }
        };
        this.updateTimeout = function (newTimeout) {
            _this.clear();
            _this.timeLeft = newTimeout;
            _this.start();
        };
        this.clear = function () {
            if (_this.timerId) {
                window.clearTimeout(_this.timerId);
            }
        };
        this.timeLeft = timeout;
        if (isBrowser) {
            this.start();
        }
    }
    return Timer;
}());
var getTransitionDuration = function (d, transitionState) {
    if (typeof d === 'object' && d !== null) {
        switch (transitionState) {
            case 'exiting':
            case 'exited':
                return d.exit || 0;
            default:
                return d.appear || d.enter || 0;
        }
    }
    return d;
};
var getFocusEvents = function (cb) {
    var handleFocus = function () {
        cb(false);
    };
    var handleBlur = function () {
        cb(true);
    };
    var handleVisibility = function () {
        cb(document.hidden);
    };
    return {
        bind: function () {
            window.addEventListener('focus', handleFocus);
            window.addEventListener('blur', handleBlur);
            document.addEventListener('visibilitychange', handleVisibility);
        },
        unbind: function () {
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('blur', handleBlur);
            document.removeEventListener('visibilitychange', handleVisibility);
        },
    };
};
var error = isDev
    ? function (message) {
        if (typeof console !== 'undefined') {
            console.error(message);
        }
        try {
            throw Error(message);
        }
        catch (err) { }
    }
    : noop;
var getDisplayName = function (WrappedComponent) {
    return WrappedComponent.displayName || WrappedComponent.name || 'Component';
};

var scrollStyles = css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  /* Chrome https://developer.mozilla.org/en-US/docs/Web/CSS/::-webkit-scrollbar */\n  &::-webkit-scrollbar {\n    width: 0.5rem;\n    height: 0.5rem;\n  }\n  &::-webkit-scrollbar-track {\n  }\n  &::-webkit-scrollbar-thumb {\n    background: gainsboro;\n  }\n  &::-webkit-scrollbar-thumb:hover,\n  &::-webkit-scrollbar-thumb:active {\n    background: lightgray;\n  }\n  /* Firefox https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Scrollbars */\n  scrollbar-color: auto;\n  scrollbar-width: thin;\n"], ["\n  /* Chrome https://developer.mozilla.org/en-US/docs/Web/CSS/::-webkit-scrollbar */\n  &::-webkit-scrollbar {\n    width: 0.5rem;\n    height: 0.5rem;\n  }\n  &::-webkit-scrollbar-track {\n  }\n  &::-webkit-scrollbar-thumb {\n    background: gainsboro;\n  }\n  &::-webkit-scrollbar-thumb:hover,\n  &::-webkit-scrollbar-thumb:active {\n    background: lightgray;\n  }\n  /* Firefox https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Scrollbars */\n  scrollbar-color: auto;\n  scrollbar-width: thin;\n"])));
var classNamePrefix = 'react-cooked-bread';
var templateObject_1;

var containerClassName = classNamePrefix + "__container";
var placementsCSS = {
    'top-left': { top: 0, left: 0 },
    'top-center': { top: 0, left: '50%', transform: 'translateX(-50%)' },
    'top-right': { top: 0, right: 0 },
    'bottom-left': { bottom: 0, left: 0 },
    'bottom-center': { bottom: 0, left: '50%', transform: 'translateX(-50%)' },
    'bottom-right': { bottom: 0, right: 0 },
};
var ToastContainer = function (_a) {
    var children = _a.children, hasToasts = _a.hasToasts, placement = _a.placement, onMouseEnter = _a.onMouseEnter, onMouseLeave = _a.onMouseLeave, styler = _a.styler, divProps = __rest(_a, ["children", "hasToasts", "placement", "onMouseEnter", "onMouseLeave", "styler"]);
    return (jsx("div", __assign({ className: containerClassName, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, css: [
            scrollStyles,
            __assign(__assign({ overflowWrap: 'break-word', wordBreak: 'break-word', boxSizing: 'border-box', maxHeight: '100%', overflow: 'hidden', padding: '1rem', pointerEvents: hasToasts ? 'auto' : 'none', position: 'fixed', zIndex: 1000 }, placementsCSS[placement]), getStylesCSS(styler, { hasToasts: hasToasts, placement: placement })),
        ] }, divProps), children));
};
if (isDev) {
    ToastContainer.propTypes = {
        hasToasts: PropTypes.bool.isRequired,
        placement: placementsProps.isRequired,
        styler: stylerProps,
        children: childrenProps,
    };
}

var transitionDurationPropsType = PropTypes.oneOfType([PropTypes.number, PropTypes.object]);
var sharedPropTypes = {
    children: childrenProps,
    type: toastTypesProps.isRequired,
    content: PropTypes.node.isRequired,
    timeout: PropTypes.number.isRequired,
    isRunning: PropTypes.bool.isRequired,
    placement: placementsProps.isRequired,
    transitionDuration: transitionDurationPropsType.isRequired,
    transitionState: transitionStatusProps.isRequired,
    onDismiss: PropTypes.func.isRequired,
};
var toastRootPropTypes = __assign(__assign({}, sharedPropTypes), { onMouseEnter: PropTypes.func.isRequired, onMouseLeave: PropTypes.func.isRequired, styles: PropTypes.object });
var toastContentPropTypes = __assign(__assign({}, sharedPropTypes), { styles: PropTypes.object.isRequired });

var rootClassName = classNamePrefix + "__toast__root";
var getTransition = function (t) {
    return "transform " + t + "ms cubic-bezier(0.2, 0, 0, 1), opacity " + t + "ms, max-height " + t + "ms ease-out";
};
var getTranslate = function (placement) {
    var pos = placement.split('-');
    var position = pos[1] === 'center' ? pos[0] : pos[1];
    var translateMap = {
        right: 'translate3d(120%, 0, 0)',
        left: 'translate3d(-120%, 0, 0)',
        bottom: 'translate3d(0, 120%, 0)',
        top: 'translate3d(0, -120%, 0)',
    };
    return translateMap[position];
};
var geToastStates = function (placement) { return ({
    entering: { transform: getTranslate(placement), opacity: 0 },
    entered: { transform: 'translate3d(0,0,0)', opacity: 1, maxHeight: 800 },
    exiting: { transform: 'scale(0.80)', opacity: 0, maxHeight: 0 },
    exited: { transform: 'scale(0.80)', opacity: 0 },
    unmounted: {},
}); };
var SlideShrinkToastRoot = function (_a) {
    var children = _a.children, type = _a.type, placement = _a.placement, transitionDuration = _a.transitionDuration, transitionState = _a.transitionState, onMouseEnter = _a.onMouseEnter, onMouseLeave = _a.onMouseLeave, styles = _a.styles;
    return (jsx("div", __assign({ className: rootClassName + " " + rootClassName + "--" + type }, (type === ToastType.ERROR
        ? {
            role: 'alert',
            'aria-live': 'assertive',
        }
        : {
            role: 'status',
            'aria-live': 'polite',
        }), { "aria-atomic": "true", css: __assign(__assign({ transform: 'scale(1)', transition: getTransition(getTransitionDuration(transitionDuration, transitionState)) }, geToastStates(placement)[transitionState]), styles), onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave }), children));
};
if (isDev) {
    SlideShrinkToastRoot.propTypes = toastRootPropTypes;
}

var rootClassName$1 = classNamePrefix + "__toast__root";
var getTransition$1 = function (t) {
    return "transform " + t + "ms cubic-bezier(0.2, 0, 0, 1), opacity " + t + "ms, max-height " + t + "ms ease-out";
};
var getTranslate$1 = function (placement) {
    var pos = placement.split('-');
    var position = pos[1] === 'center' ? pos[0] : pos[1];
    var translateMap = {
        right: 'translate3d(120%, 0, 0)',
        left: 'translate3d(-120%, 0, 0)',
        bottom: 'translate3d(0, 120%, 0)',
        top: 'translate3d(0, -120%, 0)',
    };
    return translateMap[position];
};
var geToastStates$1 = function (placement) { return ({
    entering: { transform: getTranslate$1(placement), opacity: 0 },
    entered: { transform: 'translate3d(0,0,0)', opacity: 1, maxHeight: 800 },
    exiting: { transform: getTranslate$1(placement), opacity: 0, maxHeight: 0 },
    exited: { transform: getTranslate$1(placement), opacity: 0 },
    unmounted: {},
}); };
var SlideSlideToastRoot = function (_a) {
    var children = _a.children, type = _a.type, placement = _a.placement, transitionDuration = _a.transitionDuration, transitionState = _a.transitionState, onMouseEnter = _a.onMouseEnter, onMouseLeave = _a.onMouseLeave, styles = _a.styles;
    return (jsx("div", __assign({ className: rootClassName$1 + " " + rootClassName$1 + "--" + type }, (type === ToastType.ERROR
        ? {
            role: 'alert',
            'aria-live': 'assertive',
        }
        : {
            role: 'status',
            'aria-live': 'polite',
        }), { "aria-atomic": "true", css: __assign(__assign({ transform: 'scale(1)', transition: getTransition$1(getTransitionDuration(transitionDuration, transitionState)) }, geToastStates$1(placement)[transitionState]), styles), onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave }), children));
};
if (isDev) {
    SlideSlideToastRoot.propTypes = toastRootPropTypes;
}

var rootClassName$2 = classNamePrefix + "__toast__root";
var getTransition$2 = function (t) { return "opacity " + t + "ms, max-height " + t + "ms ease-out"; };
var toastStates = {
    entering: { opacity: 0 },
    entered: { opacity: 1, maxHeight: 800 },
    exiting: { opacity: 0, maxHeight: 0 },
    exited: { opacity: 0 },
    unmounted: {},
};
var FadeToastRoot = function (_a) {
    var children = _a.children, type = _a.type, transitionDuration = _a.transitionDuration, transitionState = _a.transitionState, onMouseEnter = _a.onMouseEnter, onMouseLeave = _a.onMouseLeave, styles = _a.styles;
    return (jsx("div", __assign({ className: rootClassName$2 + " " + rootClassName$2 + "--" + type }, (type === ToastType.ERROR
        ? {
            role: 'alert',
            'aria-live': 'assertive',
        }
        : {
            role: 'status',
            'aria-live': 'polite',
        }), { "aria-atomic": "true", css: __assign(__assign({ transition: getTransition$2(getTransitionDuration(transitionDuration, transitionState)) }, toastStates[transitionState]), styles), onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave }), children));
};
if (isDev) {
    FadeToastRoot.propTypes = toastRootPropTypes;
}

var getDefaultProps = function (width, height) {
    if (height === void 0) { height = 16; }
    return {
        role: 'img',
        height: height,
        width: width,
        viewBox: "0 0 " + width + " " + height,
        xmlns: 'http://www.w3.org/2000/svg',
        style: {
            display: 'inline-block',
            verticalAlign: 'text-top',
            fill: 'currentColor',
        },
    };
};
var ids = {
    alert: 'react-cooked-bread__alert-icon-title',
    check: 'react-cooked-bread__check-icon-title',
    flame: 'react-cooked-bread__flame-icon-title',
    info: 'react-cooked-bread__info-icon-title',
    close: 'react-cooked-bread__close-icon-title',
};
var AlertIcon = function (props) { return (React.createElement("svg", __assign({ "aria-labelledby": ids.alert }, getDefaultProps(16), props),
    React.createElement("title", { id: ids.alert }, "warning"),
    React.createElement("path", { fillRule: "evenodd", d: "M8.893 1.5c-.183-.31-.52-.5-.887-.5s-.703.19-.886.5L.138 13.499a.98.98 0 0 0 0 1.001c.193.31.53.501.886.501h13.964c.367 0 .704-.19.877-.5a1.03 1.03 0 0 0 .01-1.002L8.893 1.5zm.133 11.497H6.987v-2.003h2.039v2.003zm0-3.004H6.987V5.987h2.039v4.006z" }))); };
var CheckIcon = function (props) { return (React.createElement("svg", __assign({ "aria-labelledby": ids.check }, getDefaultProps(12), props),
    React.createElement("title", { id: ids.check }, "success"),
    React.createElement("path", { fillRule: "evenodd", d: "M12 5.5l-8 8-4-4L1.5 8 4 10.5 10.5 4 12 5.5z" }))); };
var FlameIcon = function (props) { return (React.createElement("svg", __assign({ "aria-labelledby": ids.flame }, getDefaultProps(12), props),
    React.createElement("title", { id: ids.flame }, "error"),
    React.createElement("path", { fillRule: "evenodd", d: "M5.05.01c.81 2.17.41 3.38-.52 4.31C3.55 5.37 1.98 6.15.9 7.68c-1.45 2.05-1.7 6.53 3.53 7.7-2.2-1.16-2.67-4.52-.3-6.61-.61 2.03.53 3.33 1.94 2.86 1.39-.47 2.3.53 2.27 1.67-.02.78-.31 1.44-1.13 1.81 3.42-.59 4.78-3.42 4.78-5.56 0-2.84-2.53-3.22-1.25-5.61-1.52.13-2.03 1.13-1.89 2.75.09 1.08-1.02 1.8-1.86 1.33-.67-.41-.66-1.19-.06-1.78C8.18 5.01 8.68 2.15 5.05.02L5.03 0l.02.01z" }))); };
var InfoIcon = function (props) {
    return (React.createElement("svg", __assign({ "aria-labelledby": ids.info }, getDefaultProps(14), props),
        React.createElement("title", { id: ids.info }, "informational"),
        React.createElement("path", { fillRule: "evenodd", d: "M6.3 5.71a.942.942 0 0 1-.28-.7c0-.28.09-.52.28-.7.19-.18.42-.28.7-.28.28 0 .52.09.7.28.18.19.28.42.28.7 0 .28-.09.52-.28.7a1 1 0 0 1-.7.3c-.28 0-.52-.11-.7-.3zM8 8.01c-.02-.25-.11-.48-.31-.69-.2-.19-.42-.3-.69-.31H6c-.27.02-.48.13-.69.31-.2.2-.3.44-.31.69h1v3c.02.27.11.5.31.69.2.2.42.31.69.31h1c.27 0 .48-.11.69-.31.2-.19.3-.42.31-.69H8V8v.01zM7 2.32C3.86 2.32 1.3 4.86 1.3 8c0 3.14 2.56 5.7 5.7 5.7s5.7-2.55 5.7-5.7c0-3.15-2.56-5.69-5.7-5.69v.01zM7 1c3.86 0 7 3.14 7 7s-3.14 7-7 7-7-3.12-7-7 3.14-7 7-7z" })));
};
var CloseIcon = function (props) { return (React.createElement("svg", __assign({ "aria-labelledby": ids.close }, getDefaultProps(14), props),
    React.createElement("title", { id: ids.close }, "close"),
    React.createElement("path", { fillRule: "evenodd", d: "M7.71 8.23l3.75 3.75-1.48 1.48-3.75-3.75-3.75 3.75L1 11.98l3.75-3.75L1 4.48 2.48 3l3.75 3.75L9.98 3l1.48 1.48-3.75 3.75z" }))); };
var CloseThinIcon = function (props) { return (React.createElement("svg", __assign({ "aria-labelledby": ids.close }, getDefaultProps(20, 20), { viewBox: "0 0 50 50" }, props),
    React.createElement("title", { id: ids.close }, "close"),
    React.createElement("path", { d: "M37.304 11.282l1.414 1.414-26.022 26.02-1.414-1.413z" }),
    React.createElement("path", { d: "M12.696 11.282l26.022 26.02-1.414 1.415-26.022-26.02z" }))); };

var borderRadius = 4;
var typeColors = {
    success: {
        text: '#006644',
        accent: '#36B37E',
        bg: '#E3FCEF',
    },
    error: {
        text: '#BF2600',
        accent: '#FF5630',
        bg: '#FFEBE6',
    },
    warning: {
        text: '#FF8B00',
        accent: '#FFAB00',
        bg: '#FFFAE6',
    },
    info: {
        text: '#505F79',
        accent: '#2684FF',
        bg: 'white',
    },
};
var classNames = {
    wrapper: classNamePrefix + "__toast__wrapper",
    iconWrapper: classNamePrefix + "__toast__icon-wrapper",
    icon: classNamePrefix + "__toast__icon",
    progress: classNamePrefix + "__toast__progress",
    title: classNamePrefix + "__toast__title",
    subtitle: classNamePrefix + "__toast__subtitle",
    text: classNamePrefix + "__toast__text",
    closeButtonWrapper: classNamePrefix + "__toast__close-button-wrapper",
    closeButton: classNamePrefix + "__toast__close-button",
    closeButtonIcon: classNamePrefix + "__toast__close-button-icon",
};
var shrinkKeyframes = keyframes(templateObject_1$1 || (templateObject_1$1 = __makeTemplateObject(["from { height: 100%; } to { height: 0% }"], ["from { height: 100%; } to { height: 0% }"])));
var icons = {
    success: CheckIcon,
    error: FlameIcon,
    warning: AlertIcon,
    info: InfoIcon,
};
var Wrapper = function (_a) {
    var children = _a.children, type = _a.type, styles = _a.styles;
    return (jsx("div", { className: classNames.wrapper + " " + classNames.wrapper + "--" + type, css: __assign({ boxSizing: 'border-box', borderRadius: borderRadius, width: 360, maxWidth: 'calc(100vw - 2rem)', display: 'flex', backgroundColor: typeColors[type].bg, color: typeColors[type].text, boxShadow: '0 3px 8px rgba(0, 0, 0, 0.175)', marginBottom: '0.5rem' }, styles) }, children));
};
var CloseButton = function (_a) {
    var children = _a.children, onClick = _a.onClick, styles = _a.styles;
    return (jsx("button", { className: classNames.closeButton, "aria-label": "close", css: __assign({ cursor: 'pointer', color: 'black', appearance: 'none', border: 'none', background: 'transparent', display: 'flex', alignItems: 'flex-start', opacity: 0.5, padding: '0.5rem', transition: 'opacity 150ms', ':hover': { opacity: 1 } }, styles), onClick: onClick }, children));
};
var Text = function (_a) {
    var children = _a.children, styles = _a.styles;
    return (jsx("div", { className: classNames.text, css: __assign({ flexGrow: 1, fontSize: 14, lineHeight: 1.4, minHeight: 40, padding: '0.5rem 0.75rem' }, styles) }, children));
};
var Progress = function (_a) {
    var timeout = _a.timeout, opacity = _a.opacity, isRunning = _a.isRunning, styles = _a.styles;
    return (jsx("div", { className: classNames.progress, css: __assign({ animation: shrinkKeyframes + " " + timeout + "ms linear", animationPlayState: isRunning ? 'running' : 'paused', backgroundColor: 'rgba(0,0,0,0.1)', bottom: 0, height: 0, left: 0, opacity: opacity, position: 'absolute', width: '100%' }, styles) }));
};
var Icon = function (_a) {
    var children = _a.children, type = _a.type, wrapperStyles = _a.wrapperStyles, iconStyles = _a.iconStyles;
    var Glyph = icons[type];
    return (jsx("div", { className: classNames.iconWrapper, css: __assign({ backgroundColor: typeColors[type].accent, borderTopLeftRadius: borderRadius, borderBottomLeftRadius: borderRadius, color: typeColors[type].bg, flexShrink: 0, paddingBottom: '0.5rem', paddingTop: '0.5rem', position: 'relative', overflow: 'hidden', textAlign: 'center', width: 30 }, wrapperStyles) },
        children,
        jsx(Glyph, { className: classNames.icon, css: __assign({ position: 'relative', zIndex: 1 }, iconStyles) })));
};
var GlossyToastContent = function (_a) {
    var type = _a.type, autoDismiss = _a.autoDismiss, timeout = _a.timeout, isRunning = _a.isRunning, styles = _a.styles, title = _a.title, subtitle = _a.subtitle, onDismiss = _a.onDismiss, children = _a.children;
    return (jsx(Wrapper, { type: type, styles: styles === null || styles === void 0 ? void 0 : styles.wrapper },
        jsx(Icon, { type: type, wrapperStyles: styles === null || styles === void 0 ? void 0 : styles.iconWrapper, iconStyles: styles === null || styles === void 0 ? void 0 : styles.icon },
            jsx(Progress, { key: timeout, opacity: autoDismiss ? 1 : 0, timeout: timeout, isRunning: isRunning, styles: styles === null || styles === void 0 ? void 0 : styles.progress })),
        jsx(Text, { styles: styles === null || styles === void 0 ? void 0 : styles.text },
            title && (jsx(Fragment, null,
                jsx("strong", { className: classNames.title, css: __assign({}, styles === null || styles === void 0 ? void 0 : styles.title) }, title),
                ' ')),
            subtitle && (jsx(Fragment, null,
                jsx("span", { className: classNames.subtitle, css: __assign({}, styles === null || styles === void 0 ? void 0 : styles.subtitle) },
                    "(",
                    subtitle,
                    ")"),
                ' ')),
            children),
        onDismiss && (jsx("div", { css: __assign({ marginTop: '0.25rem', marginRight: '0.25rem' }, styles === null || styles === void 0 ? void 0 : styles.closeButtonWrapper) },
            jsx(CloseButton, { onClick: onDismiss, styles: styles === null || styles === void 0 ? void 0 : styles.closeButton },
                jsx(CloseIcon, { className: classNames.closeButtonIcon, css: __assign({}, styles === null || styles === void 0 ? void 0 : styles.closeButtonIcon) }))))));
};
if (isDev) {
    GlossyToastContent.propTypes = toastContentPropTypes;
}
var templateObject_1$1;

var typeColors$1 = {
    success: {
        accent: '#28a745',
    },
    error: {
        accent: '#dc3545',
    },
    warning: {
        accent: '#ffc107',
    },
    info: {
        accent: '#007bff',
    },
};
var classNames$1 = {
    wrapper: classNamePrefix + "__toast__wrapper",
    iconWrapper: classNamePrefix + "__toast__icon-wrapper",
    icon: classNamePrefix + "__toast__icon",
    progress: classNamePrefix + "__toast__progress",
    header: classNamePrefix + "__toast__header",
    title: classNamePrefix + "__toast__title",
    subtitle: classNamePrefix + "__toast__subtitle",
    text: classNamePrefix + "__toast__text",
    closeButton: classNamePrefix + "__toast__close-button",
    closeButtonIcon: classNamePrefix + "__toast__close-button-icon",
};
var shrinkKeyframes$1 = keyframes(templateObject_1$2 || (templateObject_1$2 = __makeTemplateObject(["from { width: 100%; } to { width: 0% }"], ["from { width: 100%; } to { width: 0% }"])));
var Wrapper$1 = function (_a) {
    var children = _a.children, type = _a.type, styles = _a.styles;
    return (jsx("div", { className: classNames$1.wrapper + " " + classNames$1.wrapper + "--" + type, css: __assign({ boxSizing: 'border-box', overflow: 'hidden', borderRadius: '0.25rem', width: 350, maxWidth: 'calc(100vw - 2rem)', backgroundColor: 'white', boxShadow: '0 0.1rem 0.75rem rgba(0,0,0,.1)', backdropFilter: 'blur(10px)', border: '1px solid rgba(0,0,0,.1)', marginBottom: '0.5rem', fontSize: '0.875rem' }, styles) }, children));
};
var CloseButton$1 = function (_a) {
    var children = _a.children, onClick = _a.onClick, styles = _a.styles;
    return (jsx("button", { className: classNames$1.closeButton, "aria-label": "close", css: __assign({ cursor: 'pointer', color: 'black', appearance: 'none', border: 'none', background: 'transparent', display: 'flex', alignItems: 'flex-start', opacity: 0.5, padding: '0.5rem', transition: 'opacity 150ms', marginLeft: '0.25rem', ':hover': { opacity: 1 } }, styles), onClick: onClick }, children));
};
var Header = function (_a) {
    var children = _a.children;
    return (jsx("div", { className: classNames$1.header, css: {
            colors: '#6c757d',
            display: 'flex',
            alignItems: 'center',
            padding: '0.25rem 0.75rem',
            borderBottom: '1px solid rgba(0,0,0,.05)',
        } }, children));
};
var Text$1 = function (_a) {
    var children = _a.children, styles = _a.styles;
    return (jsx("div", { className: classNames$1.text, css: __assign({ color: '#212529', position: 'relative', lineHeight: 1.4, padding: '0.75rem' }, styles) }, children));
};
var Progress$1 = function (_a) {
    var timeout = _a.timeout, opacity = _a.opacity, isRunning = _a.isRunning, styles = _a.styles;
    return (jsx("div", { className: classNames$1.progress, css: __assign({ animation: shrinkKeyframes$1 + " " + timeout + "ms linear", animationPlayState: isRunning ? 'running' : 'paused', backgroundColor: 'rgba(0,0,0,0.1)', top: 0, height: 2, left: 0, opacity: opacity, position: 'absolute', width: 0 }, styles) }));
};
var Icon$1 = function (_a) {
    var type = _a.type, hasTitle = _a.hasTitle, wrapperStyles = _a.wrapperStyles, iconStyles = _a.iconStyles;
    return (jsx("div", { className: classNames$1.iconWrapper, css: __assign({ marginRight: hasTitle ? '.5rem' : 'auto' }, wrapperStyles) },
        jsx("svg", { className: classNames$1.icon + " " + classNames$1.icon + "--" + type, css: __assign({ fontSize: '1.125rem', textAnchor: 'middle', userSelect: 'none', borderRadius: '0.25rem' }, iconStyles), width: "20px", height: "20px", xmlns: "http://www.w3.org/2000/svg", preserveAspectRatio: "xMidYMid slice", focusable: "false", role: "img" },
            jsx("rect", { width: "100%", height: "100%", fill: typeColors$1[type].accent }))));
};
var BootstrapToastContent = function (_a) {
    var type = _a.type, autoDismiss = _a.autoDismiss, timeout = _a.timeout, isRunning = _a.isRunning, styles = _a.styles, onDismiss = _a.onDismiss, title = _a.title, subtitle = _a.subtitle, children = _a.children;
    return (jsx(Wrapper$1, { type: type, styles: styles === null || styles === void 0 ? void 0 : styles.wrapper },
        jsx(Header, null,
            jsx(Icon$1, { type: type, wrapperStyles: styles === null || styles === void 0 ? void 0 : styles.iconWrapper, iconStyles: styles === null || styles === void 0 ? void 0 : styles.icon, hasTitle: !!title }),
            title && (jsx("strong", { className: classNames$1.title, css: __assign({ color: '#6c757d', marginRight: 'auto' }, styles === null || styles === void 0 ? void 0 : styles.title) }, title)),
            subtitle && (jsx("small", { className: classNames$1.subtitle, css: __assign({ color: '#6c757d', fontWeight: 400, paddingLeft: '0.25rem' }, styles === null || styles === void 0 ? void 0 : styles.subtitle) }, subtitle)),
            onDismiss && (jsx(CloseButton$1, { onClick: onDismiss, styles: styles === null || styles === void 0 ? void 0 : styles.closeButton },
                jsx(CloseIcon, { className: classNames$1.closeButtonIcon, css: __assign({}, styles === null || styles === void 0 ? void 0 : styles.closeButtonIcon) })))),
        jsx(Text$1, { styles: styles === null || styles === void 0 ? void 0 : styles.text },
            jsx(Progress$1, { key: timeout, opacity: autoDismiss ? 1 : 0, timeout: timeout, isRunning: isRunning, styles: styles === null || styles === void 0 ? void 0 : styles.progress }),
            children)));
};
if (isDev) {
    BootstrapToastContent.propTypes = toastContentPropTypes;
}
var templateObject_1$2;

var borderRadius$1 = 3;
var typeColors$2 = {
    success: '#51a351',
    error: '#bd362f',
    warning: '#f89406',
    info: '#2f96b4',
};
var classNames$2 = {
    wrapper: classNamePrefix + "__toast__wrapper",
    iconWrapper: classNamePrefix + "__toast__icon-wrapper",
    icon: classNamePrefix + "__toast__icon",
    progress: classNamePrefix + "__toast__progress",
    title: classNamePrefix + "__toast__title",
    subtitle: classNamePrefix + "__toast__subtitle",
    text: classNamePrefix + "__toast__text",
    closeButtonWrapper: classNamePrefix + "__toast__close-button-wrapper",
    closeButton: classNamePrefix + "__toast__close-button",
    closeButtonIcon: classNamePrefix + "__toast__close-button-icon",
};
var shrinkKeyframes$2 = keyframes(templateObject_1$3 || (templateObject_1$3 = __makeTemplateObject(["from { width: 100%; } to { width: 0% }"], ["from { width: 100%; } to { width: 0% }"])));
var icons$1 = {
    success: CheckIcon,
    error: FlameIcon,
    warning: AlertIcon,
    info: InfoIcon,
};
var Wrapper$2 = function (_a) {
    var children = _a.children, type = _a.type, styles = _a.styles;
    return (jsx("div", { className: classNames$2.wrapper + " " + classNames$2.wrapper + "--" + type, css: __assign({ boxSizing: 'border-box', position: 'relative', borderRadius: borderRadius$1, width: 350, maxWidth: 'calc(100vw - 2rem)', display: 'flex', backgroundColor: typeColors$2[type], color: 'white', boxShadow: '0 0 12px #999', marginBottom: '0.5rem', opacity: '0.8', ':hover': {
                opacity: '1',
                boxShadow: '0 0 12px #000',
            } }, styles) }, children));
};
var CloseButton$2 = function (_a) {
    var children = _a.children, onClick = _a.onClick, styles = _a.styles;
    return (jsx("button", { className: classNames$2.closeButton, "aria-label": "close", css: __assign({ cursor: 'pointer', color: 'white', appearance: 'none', border: 'none', background: 'transparent', display: 'flex', alignItems: 'flex-start', opacity: 1, padding: '0.5rem', transition: 'opacity 150ms', ':hover': { opacity: 0.8 } }, styles), onClick: onClick }, children));
};
var Text$2 = function (_a) {
    var children = _a.children, styles = _a.styles;
    return (jsx("div", { className: classNames$2.text, css: __assign({ flexGrow: 1, fontSize: 14, lineHeight: 1.6, minHeight: 40, paddingTop: '1rem', paddingBottom: '1rem' }, styles) }, children));
};
var Progress$2 = function (_a) {
    var timeout = _a.timeout, opacity = _a.opacity, isRunning = _a.isRunning, styles = _a.styles;
    return (jsx("div", { className: classNames$2.progress, css: __assign({ animation: shrinkKeyframes$2 + " " + timeout + "ms linear", animationPlayState: isRunning ? 'running' : 'paused', backgroundColor: 'rgba(0,0,0,0.4)', bottom: 0, height: 4, left: 0, opacity: opacity, position: 'absolute', width: 0 }, styles) }));
};
var Icon$2 = function (_a) {
    var type = _a.type, wrapperStyles = _a.wrapperStyles, iconStyles = _a.iconStyles;
    var Glyph = icons$1[type];
    return (jsx("div", { className: classNames$2.iconWrapper, css: __assign({ color: 'white', flexShrink: 0, padding: '1rem', display: 'flex', alignItems: 'center' }, wrapperStyles) },
        jsx(Glyph, { className: classNames$2.icon, width: 24, height: 24, css: __assign({}, iconStyles) })));
};
var ClassicToastContent = function (_a) {
    var type = _a.type, autoDismiss = _a.autoDismiss, timeout = _a.timeout, isRunning = _a.isRunning, styles = _a.styles, title = _a.title, subtitle = _a.subtitle, onDismiss = _a.onDismiss, children = _a.children;
    return (jsx(Wrapper$2, { type: type, styles: styles === null || styles === void 0 ? void 0 : styles.wrapper },
        jsx(Progress$2, { key: timeout, opacity: autoDismiss ? 1 : 0, timeout: timeout, isRunning: isRunning, styles: styles === null || styles === void 0 ? void 0 : styles.progress }),
        jsx(Icon$2, { type: type, wrapperStyles: styles === null || styles === void 0 ? void 0 : styles.iconWrapper, iconStyles: styles === null || styles === void 0 ? void 0 : styles.icon }),
        jsx(Text$2, { styles: styles === null || styles === void 0 ? void 0 : styles.text },
            title && (jsx(Fragment, null,
                jsx("strong", { className: classNames$2.title, css: __assign({}, styles === null || styles === void 0 ? void 0 : styles.title) }, title),
                ' ')),
            subtitle && (jsx(Fragment, null,
                jsx("span", { className: classNames$2.subtitle, css: __assign({}, styles === null || styles === void 0 ? void 0 : styles.subtitle) },
                    "(",
                    subtitle,
                    ")"),
                ' ')),
            children),
        onDismiss && (jsx("div", { css: __assign({ marginTop: '0.25rem', marginRight: '0.25rem' }, styles === null || styles === void 0 ? void 0 : styles.closeButtonWrapper) },
            jsx(CloseButton$2, { onClick: onDismiss, styles: styles === null || styles === void 0 ? void 0 : styles.closeButton },
                jsx(CloseIcon, { className: classNames$2.closeButtonIcon, css: __assign({}, styles === null || styles === void 0 ? void 0 : styles.closeButtonIcon) }))))));
};
if (isDev) {
    ClassicToastContent.propTypes = toastContentPropTypes;
}
var templateObject_1$3;

var typeStyles = {
    success: {
        color: 'white',
        backgroundColor: '#0070f3',
    },
    warning: {
        color: 'white',
        backgroundColor: '#f5a623',
    },
    error: {
        color: 'white',
        backgroundColor: '#e00',
    },
    info: {
        color: 'black',
        backgroundColor: 'white',
    },
};
var classNames$3 = {
    wrapper: classNamePrefix + "__toast__wrapper",
    progress: classNamePrefix + "__toast__progress",
    title: classNamePrefix + "__toast__title",
    subtitle: classNamePrefix + "__toast__subtitle",
    text: classNamePrefix + "__toast__text",
    buttonsWrapper: classNamePrefix + "__toast__buttons-wrapper",
    actionButton: classNamePrefix + "__toast__button",
    closeButton: classNamePrefix + "__toast__close-button",
    closeButtonIcon: classNamePrefix + "__toast__close-button-icon",
};
var shrinkKeyframes$3 = keyframes(templateObject_1$4 || (templateObject_1$4 = __makeTemplateObject(["from { width: 100%; } to { width: 0% }"], ["from { width: 100%; } to { width: 0% }"])));
var Wrapper$3 = function (_a) {
    var children = _a.children, styles = _a.styles, type = _a.type;
    return (jsx("div", { className: classNames$3.wrapper + " " + classNames$3.wrapper + "--" + type, css: __assign(__assign({ boxSizing: 'border-box', position: 'relative', marginBottom: '0.5rem', display: 'flex', width: 420, maxWidth: 'calc(100vw - 2rem)', justifyContent: 'space-between', alignItems: 'center', boxShadow: 'rgba(0, 0, 0, 0.12) 0px 5px 10px', borderRadius: '5px', padding: '1rem' }, typeStyles[type]), styles) }, children));
};
var CloseButton$3 = function (_a) {
    var children = _a.children, onClick = _a.onClick, styles = _a.styles;
    return (jsx("button", { className: classNames$3.closeButton, "aria-label": "close", css: __assign({ marginLeft: '0.5rem', cursor: 'pointer', color: 'black', appearance: 'none', border: 'none', background: 'transparent', display: 'flex', alignItems: 'flex-start', opacity: 0.5, padding: '0.5rem', transition: 'opacity 150ms', ':hover': { opacity: 1 } }, styles), onClick: onClick }, children));
};
var Progress$3 = function (_a) {
    var timeout = _a.timeout, opacity = _a.opacity, isRunning = _a.isRunning, styles = _a.styles;
    return (jsx("div", { className: classNames$3.progress, css: __assign({ animation: shrinkKeyframes$3 + " " + timeout + "ms linear", animationPlayState: isRunning ? 'running' : 'paused', backgroundColor: 'rgba(0,0,0,0.05)', borderTopLeftRadius: '5px', borderBottomLeftRadius: '5px', top: 0, height: '100%', left: 0, bottom: 0, opacity: opacity, position: 'absolute', width: 0 }, styles) }));
};
var ActionButton = function (_a) {
    var text = _a.text, onClick = _a.onClick, secondary = _a.secondary;
    return (jsx("button", { className: classNames$3.actionButton, onClick: onClick, css: {
            marginLeft: '0.5rem',
            height: '1.5rem',
            lineHeight: '1.5rem',
            minWidth: 'min-content',
            width: 'auto',
            fontWeight: 400,
            fontSize: '0.75rem',
            userSelect: 'none',
            textTransform: 'capitalize',
            display: 'inline-block',
            justifyContent: 'center',
            textAlign: 'center',
            whiteSpace: 'nowrap',
            color: secondary ? 'dimgrey' : 'white',
            backgroundColor: secondary ? 'white' : 'black',
            cursor: 'pointer',
            boxShadow: 'none',
            padding: '0px 0.625rem',
            borderRadius: '5px',
            transition: 'background-color 200ms ease 0ms, box-shadow 200ms ease 0ms, border 200ms ease 0ms, color 200ms ease 0ms',
            overflow: 'hidden',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: secondary ? 'dimgrey' : 'black',
            ':hover': {
                color: 'black',
                borderColor: 'black',
                backgroundColor: 'white',
            },
        } }, text));
};
var ZeitToastContent = function (_a) {
    var type = _a.type, autoDismiss = _a.autoDismiss, timeout = _a.timeout, isRunning = _a.isRunning, styles = _a.styles, onDismiss = _a.onDismiss, title = _a.title, subtitle = _a.subtitle, buttons = _a.buttons, children = _a.children;
    return (jsx(Wrapper$3, { type: type, styles: styles === null || styles === void 0 ? void 0 : styles.wrapper },
        jsx(Progress$3, { key: timeout, opacity: autoDismiss ? 1 : 0, timeout: timeout, isRunning: isRunning, styles: styles === null || styles === void 0 ? void 0 : styles.progress }),
        jsx("div", { className: classNames$3.text, css: __assign({ zIndex: 1 }, styles === null || styles === void 0 ? void 0 : styles.text) },
            title && (jsx(Fragment, null,
                jsx("strong", { className: classNames$3.title, css: __assign({}, styles === null || styles === void 0 ? void 0 : styles.title) }, title),
                ' ')),
            subtitle && (jsx(Fragment, null,
                jsx("span", { className: classNames$3.subtitle, css: __assign({}, styles === null || styles === void 0 ? void 0 : styles.subtitle) },
                    "(",
                    subtitle,
                    ")"),
                ' ')),
            children),
        jsx("div", { className: classNames$3.buttonsWrapper, css: __assign({ display: 'flex', alignItems: 'center', zIndex: 1 }, styles === null || styles === void 0 ? void 0 : styles.buttonsWrapper) },
            (buttons === null || buttons === void 0 ? void 0 : buttons.length) &&
                buttons.map(function (buttonProps, index) { return jsx(ActionButton, __assign({ key: index }, buttonProps)); }),
            jsx(CloseButton$3, { onClick: onDismiss, styles: styles === null || styles === void 0 ? void 0 : styles.closeButton },
                jsx(CloseThinIcon, { className: classNames$3.closeButtonIcon, css: __assign({}, styles === null || styles === void 0 ? void 0 : styles.closeButtonIcon) })))));
};
if (isDev) {
    ZeitToastContent.propTypes = toastContentPropTypes;
}
var templateObject_1$4;

var typeColors$3 = {
    success: '#dff6dd',
    info: '#deecf9',
    warning: '#fff4ce',
    error: '#fde7e9',
};
var classNames$4 = {
    wrapper: classNamePrefix + "__toast__wrapper",
    title: classNamePrefix + "__toast__title",
    subtitle: classNamePrefix + "__toast__subtitle",
    text: classNamePrefix + "__toast__text",
    closeButton: classNamePrefix + "__toast__close-button",
};
var Wrapper$4 = function (_a) {
    var children = _a.children, styles = _a.styles, type = _a.type;
    return (jsx("div", { className: classNames$4.wrapper + " " + classNames$4.wrapper + "--" + type, css: __assign({ boxSizing: 'border-box', position: 'relative', marginBottom: '0.5rem', padding: '0.80rem', width: 420, maxWidth: 'calc(100vw - 2rem)', backgroundColor: typeColors$3[type], color: 'black', border: '3px ridge' }, styles) }, children));
};
var CloseButton$4 = function (_a) {
    var children = _a.children, onClick = _a.onClick, styles = _a.styles;
    return (jsx("button", { className: classNames$4.closeButton, "aria-label": "close", css: __assign({ fontFamily: 'monospace', fontSize: 16, width: 20, height: 20, textAlign: 'center', cursor: 'pointer', color: 'black', appearance: 'none', border: 'none', background: 'transparent', opacity: 0.5, padding: 0, position: 'absolute', top: 0, right: 0, ':hover': { opacity: 1 } }, styles), onClick: onClick }, children));
};
var HtmlyToastContent = function (_a) {
    var type = _a.type, styles = _a.styles, onDismiss = _a.onDismiss, title = _a.title, subtitle = _a.subtitle, children = _a.children;
    return (jsx(Wrapper$4, { type: type, styles: styles === null || styles === void 0 ? void 0 : styles.wrapper },
        title && (jsx(Fragment, null,
            jsx("strong", { className: classNames$4.title, css: __assign({}, styles === null || styles === void 0 ? void 0 : styles.title) }, title),
            ' ')),
        subtitle && (jsx(Fragment, null,
            jsx("span", { className: classNames$4.subtitle, css: __assign({}, styles === null || styles === void 0 ? void 0 : styles.subtitle) },
                "(",
                subtitle,
                ")"),
            ' ')),
        children,
        jsx(CloseButton$4, { onClick: onDismiss, styles: styles === null || styles === void 0 ? void 0 : styles.closeButton }, "x")));
};
if (isDev) {
    HtmlyToastContent.propTypes = toastContentPropTypes;
}

var Toaster = function (_a) {
    var children = _a.children, ToastRoot = _a.toastRoot, ToastContent = _a.toastContent, type = _a.type, autoDismiss = _a.autoDismiss, timeout = _a.timeout, content = _a.content, placement = _a.placement, transitionDuration = _a.transitionDuration, transitionState = _a.transitionState, pauseAllOnHover = _a.pauseAllOnHover, isContainerHovered = _a.isContainerHovered, onDismiss = _a.onDismiss, rootStyles = _a.rootStyles, contentStyles = _a.contentStyles, id = _a.id, index = _a.index, toasts = _a.toasts, reverseColumn = _a.reverseColumn, unknownProps = __rest(_a, ["children", "toastRoot", "toastContent", "type", "autoDismiss", "timeout", "content", "placement", "transitionDuration", "transitionState", "pauseAllOnHover", "isContainerHovered", "onDismiss", "rootStyles", "contentStyles", "id", "index", "toasts", "reverseColumn"]);
    var _b = useState(autoDismiss), isRunning = _b[0], setRunning = _b[1];
    var _c = useState(false), isItemHovered = _c[0], setItemHovered = _c[1];
    var timer = useRef();
    var isParentPaused = pauseAllOnHover && isContainerHovered;
    useEffect(function () {
        if (timer.current &&
            timer.current.state !== TimerState.COMPLETED &&
            timer.current.state !== TimerState.READY) {
            timer.current.updateTimeout(timeout);
        }
    }, [timeout]);
    useEffect(function () {
        if (timeout && autoDismiss && !timer.current) {
            setRunning(true);
            timer.current = new Timer(onDismiss, timeout);
        }
    }, [autoDismiss, timeout, onDismiss]);
    useEffect(function () { var _a; return (_a = timer.current) === null || _a === void 0 ? void 0 : _a.clear; }, []);
    useEffect(function () {
        var _a, _b;
        if (isParentPaused) {
            (_a = timer.current) === null || _a === void 0 ? void 0 : _a.pause();
            setRunning(false);
        }
        else if (((_b = timer.current) === null || _b === void 0 ? void 0 : _b.state) === TimerState.PAUSED) {
            timer.current.start();
            setRunning(true);
        }
    }, [isParentPaused]);
    var handleMouseEnter = useCallback(function () {
        var _a;
        if (autoDismiss && !isParentPaused) {
            (_a = timer.current) === null || _a === void 0 ? void 0 : _a.pause();
            setRunning(false);
        }
        setItemHovered(true);
    }, [autoDismiss, isParentPaused]);
    var handleMouseLeave = useCallback(function () {
        var _a;
        if (autoDismiss && !isParentPaused) {
            (_a = timer.current) === null || _a === void 0 ? void 0 : _a.start();
            setRunning(true);
        }
        setItemHovered(false);
    }, [autoDismiss, isParentPaused]);
    var styleProps = __assign(__assign({}, unknownProps), { id: id,
        index: index,
        toasts: toasts,
        type: type,
        content: content,
        autoDismiss: autoDismiss,
        timeout: timeout,
        isRunning: isRunning,
        placement: placement,
        transitionDuration: transitionDuration,
        transitionState: transitionState,
        isContainerHovered: isContainerHovered,
        reverseColumn: reverseColumn,
        isItemHovered: isItemHovered });
    var sharedProps = __assign(__assign({}, styleProps), { onDismiss: onDismiss });
    return (React.createElement(ToastRoot, __assign({}, sharedProps, { onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, styles: getStylesCSS(rootStyles, styleProps) }), ToastContent ? (React.createElement(ToastContent, __assign({}, sharedProps, { styles: getStylesMapCSS(contentStyles, styleProps) }), children)) : (children)));
};

var useContext = React.useContext, createContext = React.createContext;
var Context = createContext({
    addToast: function () { return ''; },
    removeToast: noop,
    removeAllToasts: noop,
    updateToast: noop,
    toasts: [],
});
var useToasts = function () {
    var context = useContext(Context);
    if (!context) {
        error('The `useToasts` hook must be called from a descendent of the toast provider.');
    }
    var addToast = context.addToast, removeToast = context.removeToast, removeAllToasts = context.removeAllToasts, updateToast = context.updateToast, toasts = context.toasts;
    return {
        addToast: addToast,
        removeToast: removeToast,
        removeAllToasts: removeAllToasts,
        updateToast: updateToast,
        toasts: toasts,
    };
};
var ToastConsumer = function (_a) {
    var children = _a.children;
    return (React.createElement(Context.Consumer, null, function (context) { return children(context); }));
};
if (isDev) {
    ToastConsumer.propTypes = {
        children: PropTypes.func.isRequired,
    };
}
var withToastContext = function (Component) {
    var WrappedComponent = React.forwardRef(function (props, ref) { return (React.createElement(ToastConsumer, null, function (context) { return (
    React.createElement(Component, __assign({ ref: ref }, props, { toastContext: context }))); })); });
    WrappedComponent.displayName = "withToastContext(" + getDisplayName(Component) + ")";
    hoistNonReactStatics(WrappedComponent, Component);
    return WrappedComponent;
};

var useActiveToasts = function (_maxToasts) {
    var _a = useState([]), toasts = _a[0], setToasts = _a[1];
    var hasToasts = !!toasts.length;
    var maxToasts = Math.max(Number(_maxToasts), 0);
    var toastsRef = useRef(toasts);
    useEffect(function () {
        toastsRef.current = toasts;
    }, [toasts]);
    var removeToast = useCallback(function (id) {
        setToasts(function (prevToasts) { return prevToasts.filter(function (t) { return t.id !== id; }); });
    }, []);
    var removeAllToasts = useCallback(function () {
        setToasts([]);
    }, []);
    var addToast = useCallback(function (content, options) {
        if (options === void 0) { options = {}; }
        var _a = options.type, type = _a === void 0 ? 'info' : _a, _b = options.id, id = _b === void 0 ? getId() : _b, rest = __rest(options, ["type", "id"]);
        var newToast = __assign({ content: content, id: id, type: type }, rest);
        while (toastsRef.current.some(function (t) { return t.id === newToast.id; })) {
            newToast.id = getId();
        }
        setToasts(function (prevToasts) {
            if (maxToasts && maxToasts < prevToasts.length + 1) {
                return __spreadArrays(prevToasts, [newToast]).slice(prevToasts.length + 1 - maxToasts);
            }
            else {
                return __spreadArrays(prevToasts, [newToast]);
            }
        });
        return newToast.id;
    }, [maxToasts]);
    var updateToast = useCallback(function (id, options) {
        if (options === void 0) { options = {}; }
        setToasts(function (prevToasts) {
            var index = prevToasts.findIndex(function (t) { return t.id === id; });
            if (index !== -1) {
                var updatedToast = __assign(__assign({}, prevToasts[index]), options);
                prevToasts.splice(index, 1, updatedToast);
                return prevToasts;
            }
            return prevToasts;
        });
    }, []);
    return {
        addToast: addToast,
        hasToasts: hasToasts,
        removeAllToasts: removeAllToasts,
        removeToast: removeToast,
        toasts: toasts,
        updateToast: updateToast,
    };
};

var transitionGroupClassName = 'react-cooked-bread__toast__transition-group';
var ToastProvider = function (_a) {
    var children = _a.children, _b = _a.autoDismiss, defaultAutoDismiss = _b === void 0 ? false : _b, _c = _a.timeout, defaultTimeout = _c === void 0 ? 5000 : _c, ToastRoot = _a.toastRoot, ToastContent = _a.toastContent, _d = _a.container, Container = _d === void 0 ? ToastContainer : _d, _e = _a.placement, placement = _e === void 0 ? 'bottom-right' : _e, _f = _a.transitionDuration, defaultTransitionDuration = _f === void 0 ? { appear: 200, exit: 200 } : _f, _g = _a.pauseAllOnHover, pauseAllOnHover = _g === void 0 ? false : _g, containerStyles = _a.containerStyles, transitionGroupStyles = _a.transitionGroupStyles, toastRootStyles = _a.toastRootStyles, toastContentStyles = _a.toastContentStyles, _h = _a.reverseColumn, reverseColumn = _h === void 0 ? false : _h, pauseOnFocusLoss = _a.pauseOnFocusLoss, maxToasts = _a.maxToasts;
    var _j = useState(false), isContainerHovered = _j[0], setContainerHovered = _j[1];
    var _k = useActiveToasts(maxToasts), toasts = _k.toasts, addToast = _k.addToast, removeToast = _k.removeToast, removeAllToasts = _k.removeAllToasts, updateToast = _k.updateToast, hasToasts = _k.hasToasts;
    useEffect(function () {
        var _a = getFocusEvents(setContainerHovered), bind = _a.bind, unbind = _a.unbind;
        if (pauseOnFocusLoss) {
            bind();
        }
        return unbind;
    }, [pauseOnFocusLoss]);
    var handleMouseEnter = function () {
        setContainerHovered(true);
    };
    var handleMouseLeave = function () {
        setContainerHovered(false);
    };
    var cookedLoaf = (jsx(Container, { placement: placement, hasToasts: hasToasts, styler: containerStyles, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave },
        jsx(TransitionGroup, { className: transitionGroupClassName, css: __assign(__assign({ position: 'relative' }, (reverseColumn
                ? {
                    display: 'flex',
                    flexDirection: 'column-reverse',
                }
                : {})), getStylesCSS(transitionGroupStyles, {
                placement: placement,
                pauseAllOnHover: pauseAllOnHover,
                autoDismiss: defaultAutoDismiss,
                timeout: defaultTimeout,
                transitionDuration: defaultTransitionDuration,
            })) }, toasts.map(function (_a, index, arr) {
            var id = _a.id, _b = _a.autoDismiss, autoDismiss = _b === void 0 ? defaultAutoDismiss : _b, _c = _a.timeout, timeout = _c === void 0 ? defaultTimeout : _c, _d = _a.transitionDuration, transitionDuration = _d === void 0 ? defaultTransitionDuration : _d, onDismiss = _a.onDismiss, content = _a.content, props = __rest(_a, ["id", "autoDismiss", "timeout", "transitionDuration", "onDismiss", "content"]);
            return (jsx(Transition, { key: id, appear: true, mountOnEnter: true, timeout: transitionDuration, unmountOnExit: true }, function (transitionState) { return (jsx(Toaster, __assign({ key: id, id: id, index: index, toasts: arr, toastRoot: ToastRoot, toastContent: ToastContent, pauseAllOnHover: pauseAllOnHover, autoDismiss: autoDismiss, timeout: timeout, content: content, placement: placement, transitionDuration: transitionDuration, transitionState: transitionState, isContainerHovered: isContainerHovered, reverseColumn: reverseColumn, onDismiss: function () {
                    removeToast(id);
                    if (onDismiss) {
                        onDismiss(id);
                    }
                }, rootStyles: toastRootStyles, contentStyles: toastContentStyles }, props), content)); }));
        }))));
    var portalTarget = isBrowser && document.body;
    return (jsx(Context.Provider, { value: {
            addToast: addToast,
            removeToast: removeToast,
            removeAllToasts: removeAllToasts,
            updateToast: updateToast,
            toasts: toasts,
        } },
        children,
        portalTarget ? (createPortal(cookedLoaf, portalTarget)) : (jsx(Container, { placement: placement, hasToasts: hasToasts, styler: containerStyles }))));
};
if (isDev) {
    ToastProvider.propTypes = {
        autoDismiss: PropTypes.bool,
        timeout: PropTypes.number,
        toastRoot: PropTypes.func.isRequired,
        toastContent: PropTypes.func,
        container: PropTypes.func,
        placement: placementsProps,
        transitionDuration: transitionDurationPropsType,
        pauseAllOnHover: PropTypes.bool,
        containerStyles: stylerProps,
        transitionGroupStyles: stylerProps,
        toastRootStyles: stylerProps,
        toastContentStyles: stylerProps,
        reverseColumn: PropTypes.bool,
        pauseOnFocusLoss: PropTypes.bool,
        maxToasts: PropTypes.number,
        children: childrenProps.isRequired,
    };
}

export { BootstrapToastContent, ClassicToastContent, GlossyToastContent as DefaultToastContent, SlideShrinkToastRoot as DefaultToastRoot, FadeToastRoot, GlossyToastContent, HtmlyToastContent, Placement, SlideShrinkToastRoot, SlideSlideToastRoot, ToastConsumer, ToastContainer, ToastProvider, ToastType, ZeitToastContent, toastContentPropTypes, toastRootPropTypes, useToasts, withToastContext };
//# sourceMappingURL=index.esm.js.map
